import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KB_AUDIENCES } from "@/lib/kbAudiences";

/** Audience pills + optional per-team / per-organization narrowing. */
export function KbVisibilityPicker({ audiences, teams, orgs, onChange }) {
  const [options, setOptions] = useState({ teams: [], organizations: [] });
  const [orgFilter, setOrgFilter] = useState("");

  useEffect(() => {
    api.get("/kb/visibility/options")
      .then((r) => setOptions({ teams: r.data.teams || [], organizations: r.data.organizations || [] }))
      .catch(() => {});
  }, []);

  const toggleAudience = (val) => {
    const cur = new Set(audiences || []);
    if (cur.has(val)) cur.delete(val);
    else if (val === "everyone") return onChange({ audiences: ["everyone"] });
    else { cur.delete("everyone"); cur.add(val); }
    onChange({ audiences: [...cur] });
  };
  const toggleList = (key, val, list) => {
    const cur = new Set(list || []);
    cur.has(val) ? cur.delete(val) : cur.add(val);
    onChange({ [key]: [...cur] });
  };

  const filteredOrgs = options.organizations.filter(
    (o) => !orgFilter || (o.name || "").toLowerCase().includes(orgFilter.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="kb-visibility-picker">
      <div className="space-y-1.5">
        <Label className="text-xs">Audiences</Label>
        <div className="flex flex-wrap gap-1.5">
          {KB_AUDIENCES.map((a) => {
            const on = (audiences || []).includes(a.value);
            return (
              <button key={a.value} type="button" data-testid={`kb-ed-aud-${a.value}`}
                onClick={() => toggleAudience(a.value)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  on ? "border-primary bg-primary/10 text-primary"
                     : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                {a.label}
              </button>
            );
          })}
        </div>
        {!(audiences || []).length && (
          <p className="text-xs text-amber-600">No audience selected — only staff will see this.</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Teams {teams?.length ? `(${teams.length})` : ""}</Label>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Narrowing by team applies to staff too — only super admins see past it.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {options.teams.length === 0 && <span className="text-xs text-slate-400 dark:text-slate-500">No teams found</span>}
          {[...options.teams, ...(teams || []).filter((t) => !options.teams.includes(t))].map((t) => {
            const on = (teams || []).includes(t);
            return (
              <button key={t} type="button" data-testid={`kb-ed-team-${t}`}
                onClick={() => toggleList("target_teams", t, teams)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  on ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                     : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Organizations {orgs?.length ? `(${orgs.length})` : ""}</Label>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input data-testid="kb-ed-org-filter" value={orgFilter} placeholder="Filter organizations…"
            onChange={(e) => setOrgFilter(e.target.value)} className="h-8 pl-8 text-xs" />
        </div>
        <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-md border border-slate-100 dark:border-slate-800 p-1">
          {filteredOrgs.slice(0, 100).map((o) => {
            const on = (orgs || []).includes(o.org_id);
            return (
              <button key={o.org_id} type="button" data-testid={`kb-ed-org-${o.org_id}`}
                onClick={() => toggleList("target_orgs", o.org_id, orgs)}
                className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs transition-colors ${
                  on ? "bg-indigo-50 text-indigo-700" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                <span className="truncate">{o.name}</span>
                {on && <span className="ml-2 text-[10px] font-semibold">✓</span>}
              </button>
            );
          })}
          {filteredOrgs.length === 0 && <div className="px-2 py-1 text-xs text-slate-400 dark:text-slate-500">No matches</div>}
        </div>
      </div>
    </div>
  );
}
